import { useState, useEffect } from 'react';
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from 'framer-motion';
import { Activity } from 'lucide-react';

interface NavItem {
  name: string;
  link: string;
}

interface FloatingNavProps {
  navItems: NavItem[];
  onLoginClick?: () => void;
}

export function FloatingNav({ navItems, onLoginClick }: FloatingNavProps) {
  const { scrollYProgress } = useScroll();
  const [visible, setVisible] = useState(false);

  useMotionValueEvent(scrollYProgress, "change", (current) => {
    if (typeof current === "number") {
      const direction = current - scrollYProgress.getPrevious()!;

      if (scrollYProgress.get() < 0.05) {
        setVisible(false);
      } else {
        if (direction < 0) {
          setVisible(true);
        } else {
          setVisible(false);
        }
      }
    }
  });

  return (
    <AnimatePresence mode="wait">
      <motion.div
        initial={{
          opacity: 1,
          y: -100,
        }}
        animate={{
          y: visible ? 0 : -100,
          opacity: visible ? 1 : 0,
        }}
        transition={{
          duration: 0.2,
        }}
        className="hidden md:flex max-w-fit fixed top-10 inset-x-0 mx-auto border border-gray-200 rounded-full bg-white shadow-lg z-[5000] px-8 py-4 items-center justify-center space-x-8"
      >
        <div className="flex items-center gap-3 pr-8 border-r border-gray-200">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
            <Activity className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="text-base font-bold tracking-tight text-gray-900">
            AgeQ AI
          </span>
        </div>

        {navItems.map((navItem, idx) => (
          <a
            key={`link-${idx}`}
            href={navItem.link}
            className="relative text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
          >
            <span className="block">{navItem.name}</span>
          </a>
        ))}

        <div className="flex items-center gap-3 pl-8 border-l border-gray-200">
          <button 
            onClick={onLoginClick}
            className="px-4 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
          >
            Log in
          </button>
          <button className="px-5 py-1.5 bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-semibold rounded-full transition-colors shadow-sm">
            Try it now
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
